/**
 * Category metadata for the feedback dialog.
 *
 * Everything that differs between the three categories — icon, field labels,
 * placeholders, submit label and which fields are required — is declared here
 * so the form itself stays free of conditionals.
 */

import BugReportOutlinedIcon from '@mui/icons-material/BugReportOutlined';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import type { SvgIconComponent } from '@mui/icons-material';
import type { FeedbackCategory } from '../../types/feedback';

export interface FeedbackCategoryMeta {
  icon: SvgIconComponent;
  /** i18n key of the option label. */
  labelKey: string;
  /** i18n key of the label above the second text field. */
  detailsLabelKey: string;
  summaryPlaceholderKey: string;
  detailsPlaceholderKey: string;
  submitLabelKey: string;
  /**
   * `true` for issue/feature: summary and details are both mandatory (shown
   * with an asterisk). `false` for general feedback, where at least one of the
   * two must be filled.
   */
  fieldsRequired: boolean;
}

export const FEEDBACK_CATEGORY_META: Record<FeedbackCategory, FeedbackCategoryMeta> = {
  issue: {
    icon: BugReportOutlinedIcon,
    labelKey: 'categories.issue',
    detailsLabelKey: 'fields.stepsToReproduce',
    summaryPlaceholderKey: 'placeholders.summaryIssue',
    detailsPlaceholderKey: 'placeholders.detailsIssue',
    submitLabelKey: 'submit.issue',
    fieldsRequired: true,
  },
  feature: {
    icon: LightbulbOutlinedIcon,
    labelKey: 'categories.feature',
    detailsLabelKey: 'fields.details',
    summaryPlaceholderKey: 'placeholders.summaryFeature',
    detailsPlaceholderKey: 'placeholders.detailsFeature',
    submitLabelKey: 'submit.feature',
    fieldsRequired: true,
  },
  general: {
    icon: ChatBubbleOutlineIcon,
    labelKey: 'categories.general',
    detailsLabelKey: 'fields.details',
    summaryPlaceholderKey: 'placeholders.summaryGeneral',
    detailsPlaceholderKey: 'placeholders.detailsGeneral',
    submitLabelKey: 'submit.general',
    fieldsRequired: false,
  },
};
